import { ArrowBack } from "@mui/icons-material";
import { Box, Stack, Typography } from "@mui/joy";
import { ApiQuest, ApiQuestStatus } from "griptonite-api-client/types";
import { ReactNode, useMemo } from "react";

import { useVenueQuestsQuery } from "@/hooks/quests";
import { useCurrentVenueUuid } from "@/hooks/venues";

import { ButtonLink } from "../Links";
import AddQuestButton from "./AddQuestButton";
import LoyaltyNavToggle from "./LoyaltyNavToggle";
import { QuestCard } from "./QuestCard";

const RECENT_ENDED_COUNT = 3;

const QuestGrid = ({ quests }: { quests: ApiQuest[] }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
      gap: 2,
    }}
  >
    {quests.map((quest) => (
      <QuestCard key={quest.uuid} quest={quest} />
    ))}
  </Box>
);

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <Stack gap={1.5}>
    <Typography level="title-lg">{title}</Typography>
    {children}
  </Stack>
);

export default function QuestsOverview({ showAllEnded }: { showAllEnded?: boolean }) {
  const venueUuid = useCurrentVenueUuid();
  const { data: quests, isPending } = useVenueQuestsQuery(venueUuid);

  const byStatus = useMemo(() => {
    // The API already orders each group (see `useVenueQuestsQuery`), so grouping keeps that order.
    const result: Record<ApiQuestStatus, ApiQuest[]> = {
      ACTIVE: [],
      UPCOMING: [],
      ENDED: [],
    };
    quests?.forEach((quest) => result[quest.status].push(quest));
    return result;
  }, [quests]);

  if (showAllEnded) {
    return (
      <Stack gap={3} sx={{ height: "100%", overflowY: "auto", p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" gap={1}>
            <LoyaltyNavToggle />
            <ButtonLink
              from="/gym/$venueUuid/loyalty/quests/"
              to="."
              search={{}}
              variant="plain"
              color="neutral"
              size="sm"
              startDecorator={<ArrowBack />}
            >
              Quests
            </ButtonLink>
            <Typography level="h3">Ended quests</Typography>
          </Stack>
        </Stack>
        <QuestGrid quests={byStatus.ENDED} />
        <Box>
          <AddQuestButton />
        </Box>
      </Stack>
    );
  }

  const recentEnded = byStatus.ENDED.slice(0, RECENT_ENDED_COUNT);
  const isEmpty = !isPending && !quests?.length;

  return (
    <Stack gap={4} sx={{ height: "100%", overflowY: "auto", p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" gap={1}>
          <LoyaltyNavToggle />
          <Typography level="h3">Quests</Typography>
        </Stack>
      </Stack>
      {isEmpty ? (
        <Typography textColor="neutral.500">
          No quests yet. Add one to start rewarding your members.
        </Typography>
      ) : null}
      {byStatus.ACTIVE.length ? (
        <Section title="Active">
          <QuestGrid quests={byStatus.ACTIVE} />
        </Section>
      ) : null}
      {byStatus.UPCOMING.length ? (
        <Section title="Upcoming">
          <QuestGrid quests={byStatus.UPCOMING} />
        </Section>
      ) : null}
      {byStatus.ENDED.length ? (
        <Section title="Ended">
          <QuestGrid quests={recentEnded} />
          {byStatus.ENDED.length > recentEnded.length ? (
            <Box>
              <ButtonLink
                from="/gym/$venueUuid/loyalty/quests/"
                to="."
                search={{ status: "ended" }}
                variant="outlined"
                color="neutral"
              >
                View all ended quests ({byStatus.ENDED.length})
              </ButtonLink>
            </Box>
          ) : null}
        </Section>
      ) : null}
      <Box>
        <AddQuestButton />
      </Box>
    </Stack>
  );
}
